export const ENTITY_TYPES = [
  { label: "Individual / Sole trader", value: "INDIVIDUAL" },
  { label: "Company", value: "COMPANY" },
  { label: "Partnership", value: "PARTNERSHIP" },
  { label: "Trust", value: "TRUST" },
];

const BASE_STEPS = [
  { id: "profile", title: "Profile", short: "You", hint: "Who you are and how we reach you" },
  { id: "business", title: "Business", short: "Business", hint: "Entity type and tax identifiers" },
  { id: "address", title: "Address", short: "Address", hint: "Where you’re based" },
  { id: "bank", title: "Bank", short: "Bank", hint: "Where we’ll send referral payments" },
  {
    id: "documents",
    title: "Documents",
    short: "Docs",
    hint: "Upload ID and passport for verification",
  },
];

const ACCOUNT_STEP = {
  id: "account",
  title: "Account & terms",
  short: "Finish",
  hint: "Password and partnership agreement",
};

const TERMS_STEP = { id: "account", title: "Terms", short: "Finish", hint: "Partnership agreement" };

/** A signed-in staff member already has a password, so their last step is the terms alone. */
export const stepsFor = ({ requirePassword }) => [
  ...BASE_STEPS,
  requirePassword ? ACCOUNT_STEP : TERMS_STEP,
];

export const DOC_SLOTS = [
  { key: "photoId", label: "Photo ID", hint: "Driver licence or other photo ID", required: true },
  { key: "passport", label: "Passport", hint: "Clear scan of the passport bio page", required: false },
  {
    key: "abnProof",
    label: "ABN proof",
    hint: "Optional: registration or ABN lookup screenshot",
    required: false,
  },
  {
    key: "bankProof",
    label: "Bank proof",
    hint: "Optional: statement or screenshot showing account name",
    required: false,
  },
];

export const DOC_ACCEPT = "image/jpeg,image/png,image/webp,application/pdf,.pdf,.jpg,.jpeg,.png";
export const DOC_MAX_BYTES = 10 * 1024 * 1024;

export const INITIAL_FORM = {
  fullName: "",
  tradingName: "",
  email: "",
  phone: "",
  entityType: "INDIVIDUAL",
  abn: "",
  tfn: "",
  gstRegistered: "no",
  addressLine1: "",
  addressLine2: "",
  suburb: "",
  state: "",
  postcode: "",
  country: "",
  bankAccountName: "",
  bsb: "",
  accountNumber: "",
  photoId: null,
  passport: null,
  abnProof: null,
  bankProof: null,
  password: "",
  confirmPassword: "",
  acceptTerms: false,
};

export const digitsOnly = (value) => String(value || "").replace(/\D/g, "");
const emailOk = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

/**
 * Field errors for one step. A locked field came from the office's own records and cannot be
 * edited here, so it is never flagged; a document already on file counts as provided.
 */
export const validateStep = (stepId, form, { requirePassword, isLocked, onFile }) => {
  const next = {};
  const check = (field, problem) => {
    if (isLocked(field)) return;
    const message = problem(form[field]);
    if (message) next[field] = message;
  };
  if (stepId === "profile") {
    check("fullName", (v) => (!v.trim() ? "Legal name is required" : null));
    check("email", (v) => (!v.trim() ? "Email is required" : !emailOk(v) ? "Enter a valid email" : null));
    check("phone", (v) => (!v.trim() ? "Phone is required" : null));
  }
  if (stepId === "business") {
    check("entityType", (v) => (!v ? "Select an entity type" : null));
    check("abn", (v) => (digitsOnly(v) && digitsOnly(v).length !== 11 ? "ABN must be 11 digits" : null));
    check("tfn", (v) => {
      const tfn = digitsOnly(v);
      return tfn && (tfn.length < 8 || tfn.length > 9) ? "TFN must be 8 or 9 digits" : null;
    });
  }
  if (stepId === "address") {
    check("addressLine1", (v) => (!v.trim() ? "Address is required" : null));
    check("suburb", (v) => (!v.trim() ? "Suburb is required" : null));
    check("state", (v) => (!v.trim() ? "State is required" : null));
    check("postcode", (v) => {
      if (!v.trim()) return "Postcode is required";
      return /^\d{4}$/.test(v.trim()) ? null : "Postcode must be 4 digits";
    });
  }
  if (stepId === "bank") {
    check("bankAccountName", (v) => (!v.trim() ? "Account name is required" : null));
    check("bsb", (v) => {
      const bsb = digitsOnly(v);
      if (!bsb) return "BSB is required";
      return bsb.length !== 6 ? "BSB must be 6 digits" : null;
    });
    check("accountNumber", (v) => (!digitsOnly(v) ? "Account number is required" : null));
  }
  if (stepId === "documents") {
    DOC_SLOTS.forEach((slot) => {
      if (slot.required && !form[slot.key] && !onFile(slot.key)) {
        next[slot.key] = `${slot.label} is required`;
      }
    });
  }
  if (stepId === "account") {
    if (requirePassword) {
      if (!form.password) next.password = "Create a password";
      else if (form.password.length < 10) next.password = "Use at least 10 characters";
      else if (!/[A-Za-z]/.test(form.password) || !/[^A-Za-z]/.test(form.password))
        next.password = "Mix letters with numbers or symbols";
      if (form.password !== form.confirmPassword) next.confirmPassword = "Passwords do not match";
    }
    if (!form.acceptTerms) next.acceptTerms = "You must agree to the terms to continue";
  }
  return next;
};

/** A staff member's known details as form values; TFN and account number are only last-four. */
export const formFromStaffValues = (values = {}) => ({
  ...INITIAL_FORM,
  fullName: values.fullName || "",
  email: values.email || "",
  tradingName: values.tradingName || "",
  phone: values.phone || "",
  entityType: values.entityType || INITIAL_FORM.entityType,
  abn: values.abn || "",
  tfn: values.tfnLast4 ? `•••• ${values.tfnLast4}` : "",
  gstRegistered: values.gstRegistered ? "yes" : "no",
  addressLine1: values.addressLine1 || "",
  addressLine2: values.addressLine2 || "",
  suburb: values.suburb || "",
  state: values.state || "",
  postcode: values.postcode || "",
  country: values.country || "",
  bankAccountName: values.bankAccountName || "",
  bsb: values.bsb || "",
  accountNumber: values.accountNumberLast4 ? `•••• ${values.accountNumberLast4}` : "",
});

export const DEFAULT_TERMS = `
These Referral Partner Terms & Conditions govern your participation in the organisation’s referral program.

1. Role. You refer prospective clients to the organisation. You are an independent contractor, not an employee.

2. Accuracy. You must provide true and complete profile and bank details, and keep them up to date. ABN and TFN may be provided when available.

3. Confidentiality. Client information you handle must stay confidential and only be used for authorised referral activity.

4. Payments. Referral fees are paid according to the organisation’s published rules after verification and eligibility. The organisation may adjust or withhold payments for errors, fraud, or chargebacks.

5. Compliance. You must comply with Australian law, including privacy and anti-spam rules, and must not misrepresent the organisation’s services.

6. Termination. Either party may end the partnership. Outstanding verified fees remain payable subject to the payment rules.

7. Updates. The organisation may update these terms; continued use after notice means you accept the updated version.
`.trim();
